import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Shield, Clock, TrendingUp, Gift, Bitcoin, ArrowRight,
  Apple, Smartphone, CheckCircle2, Star, Headphones,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import api, { ngn } from "@/lib/api";

const features = [
  { icon: Zap, title: "Lightning Fast", desc: "Complete any trade in under 2 minutes. No waiting, no stress." },
  { icon: Shield, title: "Bank-Grade Security", desc: "Your funds and data are protected with end-to-end encryption." },
  { icon: TrendingUp, title: "Best Market Rates", desc: "Guaranteed highest payout rates for crypto and gift cards in Nigeria." },
  { icon: Clock, title: "24/7 Support", desc: "Live chat + WhatsApp support available any time, day or night." },
];

const testimonials = [
  { name: "Chidi O.", city: "Lagos", text: "Sold my Steam card and got paid in 90 seconds. TravisB is the real deal!", rating: 5 },
  { name: "Amaka N.", city: "Abuja", text: "Best USDT rates I've found. The dashboard is clean and the chat support is instant.", rating: 5 },
  { name: "Tunde A.", city: "Ibadan", text: "I trade gift cards weekly here. Fast payouts every single time.", rating: 5 },
];

export default function Home() {
  const [rates, setRates] = useState([]);

  useEffect(() => {
    api.get("/crypto/rates").then((r) => setRates(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div className="tb-glow min-h-screen">
      <Header />

      {/* HERO */}
      <section className="pt-28 sm:pt-36 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 tb-live-dot" /> Live in Nigeria
            </span>
            <h1 data-testid="hero-title" className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              The <span className="tb-gradient-text">Fastest</span> Crypto & Giftcard Exchange
            </h1>
            <p className="mt-6 text-base sm:text-lg text-gray-300 leading-relaxed max-w-xl">
              Instantly trade Bitcoin, USDT, and gift cards (iTunes, Steam, Amazon, Sephora) to Naira in 2 minutes — with guaranteed highest market rates.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/signup" data-testid="hero-trade-now-button" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold tb-btn-primary">
                Trade Now <ArrowRight size={18} />
              </Link>
              <Link to="/rates" data-testid="hero-check-rates-button" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-blue-500/40 text-white hover:bg-blue-500/10 transition-colors">
                Check Live Rates
              </Link>
            </div>

            <div className="mt-8">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Download the app</p>
              <div className="flex flex-wrap gap-3">
                {[{ icon: Apple, label: "iOS App Store", id: "ios" }, { icon: Smartphone, label: "Google Play", id: "android" }].map((p) => (
                  <button key={p.id} data-testid={`download-app-${p.id}-button`} className="relative flex items-center gap-3 px-5 py-2.5 rounded-xl bg-[#101C3F] border border-blue-500/20 text-left opacity-90">
                    <p.icon size={22} className="text-blue-300" />
                    <span className="leading-tight">
                      <span className="block text-[10px] text-gray-400">Download on</span>
                      <span className="block text-sm font-semibold text-white">{p.label}</span>
                    </span>
                    <span className="ml-2 text-[9px] font-bold uppercase bg-blue-500/20 text-blue-200 rounded px-1.5 py-0.5">Coming Soon</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Live rate card */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="tb-card p-5 sm:p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-head font-semibold text-white flex items-center gap-2"><Bitcoin size={18} className="text-blue-300" /> Live Crypto Rates</h3>
              <span className="flex items-center gap-1.5 text-xs text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-400 tb-live-dot" /> Live</span>
            </div>
            <div className="space-y-3">
              {rates.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">Loading rates…</p>}
              {rates.map((r) => (
                <div key={r.coin} data-testid={`hero-rate-${r.symbol}`} className="flex items-center justify-between p-3 rounded-xl bg-[#17244F]/50 hover:bg-[#17244F] transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center font-mono-tb text-blue-200 text-xs font-bold">{r.symbol}</span>
                    <span className="text-sm font-medium text-white">{r.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-tb font-bold text-emerald-400 text-sm">{ngn(r.price_ngn)}</div>
                    {r.change_24h != null && (
                      <div className={`text-xs ${r.change_24h >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {r.change_24h >= 0 ? "▲" : "▼"} {Math.abs(r.change_24h)}%
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Link to="/rates" className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold text-blue-300 hover:text-blue-200">
              View all rates <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["₦2.5B+", "Traded volume"], ["50k+", "Happy traders"], ["< 2 min", "Avg. payout"], ["4.9/5", "User rating"]].map(([v, l]) => (
            <div key={l} className="tb-card p-5 text-center">
              <div className="font-head text-2xl sm:text-3xl font-extrabold tb-gradient-text">{v}</div>
              <div className="text-xs sm:text-sm text-gray-400 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center mb-14">
          <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Why traders choose TravisB</h2>
          <p className="mt-3 text-gray-400 max-w-2xl mx-auto">Built for speed, security and the best rates — designed mobile-first for Nigeria.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="tb-card p-6 hover:bg-[#17244F] transition-colors">
              <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-500/15 text-blue-300 mb-4"><f.icon size={22} /></span>
              <h3 className="font-head font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GIFTCARD CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="tb-card p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center relative overflow-hidden">
          <div className="relative z-10">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-400 mb-4"><Gift size={14} /> Gift Cards</span>
            <h2 className="font-head text-2xl sm:text-3xl font-bold text-white mb-3">Sell any gift card in seconds</h2>
            <p className="text-gray-400 mb-6">Upload your card image, get an instant Naira quote, and reach our vendor directly on WhatsApp for the fastest payout.</p>
            <ul className="space-y-2 mb-6">
              {["Steam, Amazon, iTunes, Sephora & more", "Instant payout quote in NGN", "WhatsApp fallback + live chat"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle2 size={16} className="text-emerald-400" /> {t}</li>
              ))}
            </ul>
            <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold tb-btn-primary">Start trading <ArrowRight size={16} /></Link>
          </div>
          <div className="relative z-10">
            <img src="https://images.unsplash.com/photo-1639133694967-640f255f10fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Gift cards" className="rounded-2xl w-full h-64 object-cover border border-blue-500/20" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="text-center mb-12">
          <h2 className="font-head text-2xl sm:text-3xl font-bold text-white">Loved by traders nationwide</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.name} data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`} className="tb-card p-6">
              <div className="flex gap-1 mb-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={16} className="text-amber-400 fill-amber-400" />)}</div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-200 text-sm font-bold">{t.name[0]}</span>
                <div>
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
